import { ArrowLeft, ArrowRight, OpenInNew, VisibilityOff } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DownloadIcon from '@mui/icons-material/Download';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    IconButton,
    Paper,
    Snackbar,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import { Form, Formik } from 'formik';
import Lottie from 'lottie-react'; // Correct import statement
import { DateTime } from 'luxon';
import React, { useCallback, useEffect, useState } from 'react';
import {
    Area,
    AreaChart,
    Bar,
    Brush,
    CartesianGrid,
    ComposedChart,
    ReferenceArea,
    ReferenceLine,
    ResponsiveContainer,
    Scatter,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { getAllRawDataForPoints } from '../../../app.functions';
import { Can, TypographyTitle } from '../../../components';
import CustomTooltipRawGpsData from '../../../components/Charting/CustomTooltipRawGpsData';
import saveButton from '../../../lottie-animations/Save.json';
import {
    createCrop,
    deleteCrop,
    getAllCrops,
    getFilteredExportUrl,
    getRawUploadDataFromUrl,
    updateCrop,
    trimSession,
} from '../api/performance.api';
import { formatNumber, formatTimestampSeconds, formatTimestampTenths } from '../community.func';
import {
    Crop,
    ExclusionZone,
    PerformanceDataUpload,
    RawDataPointEnhanced,
} from '../community.types';
import CropDataTable from './CropDataTable';
import ExclusionZoneDialog from './ExclusionZoneDialog';

interface RawDataViewerProps {
    upload: PerformanceDataUpload;
    distanceUnit: 'yd' | 'ft' | 'm';
    onRecalculating?: () => void;
}

const sortCrops = (crops: Crop[]): Crop[] => {
    return crops.sort((a, b) => {
        return a.start_timestamp - b.start_timestamp;
    });
};

const StrikedEditIcon = styled(EditIcon)(({ theme }) => ({
    position: 'relative',
    '&:after': {
        content: '""',
        position: 'absolute',
        top: '50%',
        left: 0,
        right: 0,
        height: '2px',
        backgroundColor: theme.palette.text.primary,
    },
}));

// Custom shape for draggable trim handles
const RoundedRectHandle = (props: any) => {
    const { cx = 0, cy = 0, fill, stroke, strokeWidth } = props;
    const width = 30; // Width of the handle (wider than the line)
    const height = 50; // Height of the handle
    const radius = height / 2; // Fully rounded corners

    return (
        <g>
            <rect
                x={cx - width / 2}
                y={cy - height / 2}
                width={width}
                height={height}
                rx={radius}
                ry={radius}
                fill={fill || stroke}
                fillOpacity={0.8}
                stroke={stroke}
                strokeWidth={strokeWidth || 2}
                style={{ cursor: 'ew-resize' }}
                onMouseEnter={(e: any) => {
                    e.target.setAttribute('fillOpacity', '1');
                    e.target.setAttribute('strokeWidth', '3');
                }}
                onMouseLeave={(e: any) => {
                    e.target.setAttribute('fillOpacity', '0.8');
                    e.target.setAttribute('strokeWidth', strokeWidth || '2');
                }}
            />
        </g>
    );
};

const RawDataViewer = ({ upload, distanceUnit, onRecalculating }: RawDataViewerProps) => {
    const [rawData, setRawData] = useState<RawDataPointEnhanced[]>([]);
    const [selectedCrops, setSelectedCrops] = useState<{
        [key: string]: {
            crop: Crop;
            data: RawDataPointEnhanced[];
            startIndex: number;
            endIndex: number;
            isEditing: boolean;
        };
    }>({});
    const [sampledData, setSampledData] = useState<RawDataPointEnhanced[]>([]);
    const [sampledDataIndexed, setSampledDataIndexed] = useState<RawDataPointEnhanced[]>([]);
    const [saveCropOpen, setSaveCropOpen] = useState(false);
    const [crops, setCrops] = useState<Crop[]>([]);
    const [cropLabel, setCropLabel] = useState<string>('');
    const [startIndex, setStartIndex] = useState<number>(0);
    const [endIndex, setEndIndex] = useState<number>(0);
    const [chartKey, setChartKey] = useState<string>('initial-key');
    const [rawDataLoading, setRawDataLoading] = useState<boolean>(false);
    const [refAreaLeft, setRefAreaLeft] = useState<number>();
    const [refAreaRight, setRefAreaRight] = useState<number>();
    const [message, setMessage] = useState<string>('');
    const [isEditMode, setIsEditMode] = useState<boolean>(false);
    const [editStartTimestamp, setEditStartTimestamp] = useState<number | null>(null);
    const [editEndTimestamp, setEditEndTimestamp] = useState<number | null>(null);
    const [originalStartTimestamp, setOriginalStartTimestamp] = useState<number | null>(null);
    const [originalEndTimestamp, setOriginalEndTimestamp] = useState<number | null>(null);
    const [isTrimming, setIsTrimming] = useState<boolean>(false);
    const [trimConfirmOpen, setTrimConfirmOpen] = useState<boolean>(false);
    const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
    const [trimWarning, setTrimWarning] = useState<string | null>(null);
    const [exclusionZones, setExclusionZones] = useState<ExclusionZone[]>([]);
    const [exclusionDialogOpen, setExclusionDialogOpen] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    const handleExportFiltered = useCallback(() => {
        setIsExporting(true);
        getFilteredExportUrl(upload.individual.uuid, upload.uuid)
            .then((response) => {
                // Trigger browser download by opening the signed URL
                const link = document.createElement('a');
                link.href = response.data.url;
                link.download = `${upload.file_name || 'session-data'}-filtered.csv`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                setMessage('Export download started');
            })
            .catch(() => {
                setMessage('Error generating export');
            })
            .finally(() => {
                setIsExporting(false);
            });
    }, [upload]);

    const handleDialogSubmit = useCallback(() => {
        createCrop(upload.individual.uuid, upload.uuid, {
            start_timestamp: sampledDataIndexed[startIndex]['Timestamp'],
            end_timestamp: sampledDataIndexed[endIndex]['Timestamp'],
            label: cropLabel,
        })
            .then((response) => {
                setCrops((crops) => sortCrops([...crops, response.data]));
                setMessage('Crop Saved Successfully');
            })
            .catch(() => {
                setMessage('Error Saving Crop');
            })
            .finally(() => {
                setCropLabel('');
                setSaveCropOpen(false);
            });
    }, [upload, sampledDataIndexed, startIndex, endIndex, cropLabel]);

    const hasAtLeastOneHeartRateValue = useCallback(() => {
        return (
            rawData.findIndex(
                (v) => v['Heart Rate (bpm)'] !== 0 && v['Heart Rate (bpm)'] !== null,
            ) > -1
        );
    }, [rawData]);

    const getDataForCrop = useCallback(
        (crop: Crop) => {
            if (!rawData || rawData.length === 0) {
                return [];
            }

            const firstTimestamp = rawData[0]?.['Timestamp'];
            const lastTimestamp = rawData[rawData.length - 1]?.['Timestamp'];

            if (firstTimestamp === undefined || lastTimestamp === undefined) {
                return [];
            }

            // Find start index - use exact match if available, otherwise find closest timestamp >= crop.start_timestamp
            let startIndex = rawData.findIndex((p) => p['Timestamp'] === crop.start_timestamp);

            // If exact match not found, find the first data point with timestamp >= crop.start_timestamp
            if (startIndex === -1) {
                // Find the index of the first point that's >= crop.start_timestamp
                for (let i = 0; i < rawData.length; i++) {
                    if (rawData[i]['Timestamp'] >= crop.start_timestamp) {
                        startIndex = i;
                        break;
                    }
                }
                // If no point found >= start_timestamp (crop start is before first data point),
                // use the first data point as the start
                if (startIndex === -1) {
                    startIndex = 0;
                }
            }

            // Find end index - use exact match if available, otherwise find closest timestamp <= crop.end_timestamp
            let endIndex = rawData.findIndex((p) => p['Timestamp'] === crop.end_timestamp);

            // If exact match not found, find the last data point with timestamp <= crop.end_timestamp
            if (endIndex === -1) {
                // Find the index of the last point that's <= crop.end_timestamp
                for (let i = rawData.length - 1; i >= startIndex; i--) {
                    if (rawData[i]['Timestamp'] <= crop.end_timestamp) {
                        endIndex = i;
                        break;
                    }
                }
                // If no point found <= end_timestamp (crop end is after last data point),
                // use the last available point
                if (endIndex === -1 && rawData.length > 0) {
                    endIndex = rawData.length - 1;
                }
            }

            // If still no valid end index, return empty array
            if (endIndex === -1 || endIndex < startIndex) {
                return [];
            }

            const cropData = rawData.slice(startIndex, endIndex + 1);

            return getAllRawDataForPoints(cropData);
        },
        [rawData],
    );

    // Check if a crop's data exists in the current rawData
    // A crop is valid if:
    // 1. Start timestamp is at or before the last timestamp (can be before first if adjusted to session start)
    // 2. End timestamp is at or after the first timestamp (can be after last if adjusted to session end)
    // 3. Start < End
    // 4. There's overlap between crop range and data range
    const isCropValid = useCallback(
        (crop: Crop): boolean => {
            if (!rawData || rawData.length === 0) {
                return false;
            }

            const firstTimestamp = rawData[0]?.['Timestamp'];
            const lastTimestamp = rawData[rawData.length - 1]?.['Timestamp'];

            if (firstTimestamp === undefined || lastTimestamp === undefined) {
                return false;
            }

            // Basic validation: start must be < end
            if (crop.end_timestamp <= crop.start_timestamp) {
                return false;
            }

            // Check if there's any overlap between crop range and data range
            // Crop overlaps if: crop.start <= lastTimestamp AND crop.end >= firstTimestamp
            const hasOverlap =
                crop.start_timestamp <= lastTimestamp && crop.end_timestamp >= firstTimestamp;

            if (!hasOverlap) {
                return false;
            }

            // If we have overlap, the crop is valid
            // The getDataForCrop function will handle finding the closest data points
            return true;
        },
        [rawData],
    );

    const saveCrop = useCallback(
        (k: string) => {
            const crop = selectedCrops[k];
            updateCrop(upload.individual.uuid, upload.uuid, crop.crop.uuid, {
                label: crop.crop.label,
                start_timestamp: crop.data[crop.startIndex]['Timestamp'],
                end_timestamp: crop.data[crop.endIndex]['Timestamp'],
            })
                .then((response) => {
                    setCrops((crops) => {
                        const newCrops = [...crops];
                        const cropIndex = newCrops.findIndex((c) => c.uuid === response.data.uuid);
                        newCrops[cropIndex] = response.data;
                        return sortCrops(newCrops);
                    });
                    setSelectedCrops((crops) => {
                        const newCrops = { ...crops };
                        const data = getDataForCrop(response.data);
                        newCrops[k].crop = response.data;
                        newCrops[k].data = data;
                        newCrops[k].startIndex = 0;
                        newCrops[k].endIndex = data.length - 1;
                        return newCrops;
                    });
                    setMessage('Crop Saved Successfully');
                })
                .catch(() => {
                    setMessage('Error Saving Crop');
                })
                .finally(() => {});
        },
        [selectedCrops, upload, getDataForCrop],
    );
    const brushChangeHandler = (newIndex: any): void => {
        setStartIndex(newIndex.startIndex);
        setEndIndex(newIndex.endIndex);
    };

    useEffect(() => {
        setSampledDataIndexed(sampledData.filter(Boolean));
        setEndIndex(sampledData.length - 1);
    }, [sampledData]);

    useEffect(() => {
        if (upload && upload.raw_data_url) {
            getAllCrops(upload.individual.uuid, upload.uuid).then((cropsResponse) => {
                const allCrops = cropsResponse.data;
                const isExclusionLabel = (label: string) =>
                    label.startsWith('__exclusion__') || label.startsWith('__trim__');
                const exclusions = allCrops.filter((c) => isExclusionLabel(c.label));
                const explorationCrops = allCrops.filter((c) => !isExclusionLabel(c.label));
                setExclusionZones(exclusions);
                setCrops(sortCrops(explorationCrops));
            });
            setRawDataLoading(true);
            getRawUploadDataFromUrl(upload.raw_data_url)
                .then((response) => {
                    let sampledData: RawDataPointEnhanced[] = [];
                    let enhancedData = getAllRawDataForPoints(response.data).filter(
                        (p) => p['Velocity (m/s)'] >= 0 && p['Velocity (mph)'] >= 0,
                    );
                    setRawData(enhancedData);
                    for (let i = 0; i < enhancedData.length; i++) {
                        if (
                            i % 10 == 0 ||
                            +enhancedData[i]['Velocity (m/s)'].toFixed(3) ===
                                upload.summary_max_velocity
                        ) {
                            sampledData.push({
                                ...enhancedData[i],
                            });
                        }
                    }
                    setSampledData(sampledData);
                })
                .catch((e) => {
                    console.log(e);
                })
                .finally(() => {
                    setRawDataLoading(false);
                });
        }
    }, [upload]);

    // Initialize edit timestamps when entering edit mode
    useEffect(() => {
        if (isEditMode && sampledDataIndexed.length > 0) {
            if (editStartTimestamp === null && editEndTimestamp === null) {
                const firstTimestamp = sampledDataIndexed[0]['Timestamp'];
                const lastTimestamp =
                    sampledDataIndexed[sampledDataIndexed.length - 1]['Timestamp'];
                setEditStartTimestamp(firstTimestamp);
                setEditEndTimestamp(lastTimestamp);
                setOriginalStartTimestamp(firstTimestamp);
                setOriginalEndTimestamp(lastTimestamp);
            }
        } else if (!isEditMode) {
            // Reset when exiting edit mode
            setEditStartTimestamp(null);
            setEditEndTimestamp(null);
            setOriginalStartTimestamp(null);
            setOriginalEndTimestamp(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditMode, sampledDataIndexed]);

    // Add cursor styles for draggable reference lines
    useEffect(() => {
        if (isEditMode) {
            const styleId = 'recharts-draggable-lines-style';
            if (!document.getElementById(styleId)) {
                const style = document.createElement('style');
                style.id = styleId;
                style.textContent = `
                    .recharts-reference-line-line[stroke="green"],
                    .recharts-reference-line-line[stroke="red"] {
                        cursor: ew-resize !important;
                    }
                    .recharts-reference-line-line[stroke="green"]:hover,
                    .recharts-reference-line-line[stroke="red"]:hover {
                        stroke-width: 4 !important;
                    }
                `;
                document.head.appendChild(style);
            }
            return () => {
                const styleElement = document.getElementById(styleId);
                if (styleElement) {
                    document.head.removeChild(styleElement);
                }
            };
        }
    }, [isEditMode]);

    const zoom = useCallback(() => {
        const startIx = sampledDataIndexed.findIndex((v) => v['Timestamp'] === refAreaLeft);
        const endIx = sampledDataIndexed.findIndex((v) => v['Timestamp'] === refAreaRight);

        if (startIx > -1 && endIx > -1) {
            setStartIndex(startIx);
            setEndIndex(endIx);
            setRefAreaLeft(0);
            setRefAreaRight(0);
            setChartKey(Math.random().toString());
        }
    }, [refAreaLeft, refAreaRight, sampledDataIndexed]);

    const handleOpenEditDialog = useCallback(() => {
        setEditDialogOpen(true);
        setIsEditMode(true);
    }, []);

    const handleCloseEditDialog = useCallback(() => {
        setEditDialogOpen(false);
        setIsEditMode(false);
        setEditStartTimestamp(null);
        setEditEndTimestamp(null);
        setOriginalStartTimestamp(null);
        setOriginalEndTimestamp(null);
    }, []);

    const handleTrimSave = useCallback(() => {
        if (!editStartTimestamp || !editEndTimestamp) {
            setMessage('Please set both start and end points');
            return;
        }

        if (editStartTimestamp >= editEndTimestamp) {
            setMessage('Start timestamp must be before end timestamp');
            return;
        }

        // Validate that the trim range is reasonable (at least 1 second)
        const trimDuration = (editEndTimestamp - editStartTimestamp) / 1000; // Convert to seconds
        if (trimDuration < 1) {
            setMessage('Trim range must be at least 1 second');
            return;
        }

        // Warn if trimming more than 95% of the session
        if (sampledDataIndexed && sampledDataIndexed.length > 0) {
            const sessionStart = sampledDataIndexed[0]['Timestamp'];
            const sessionEnd = sampledDataIndexed[sampledDataIndexed.length - 1]['Timestamp'];
            const sessionDuration = (sessionEnd - sessionStart) / 1000;
            const trimPercentage = (trimDuration / sessionDuration) * 100;

            if (trimPercentage < 5) {
                setTrimWarning(
                    `Warning: You are trimming to only ${trimPercentage.toFixed(
                        1,
                    )}% of the session (${trimDuration.toFixed(
                        1,
                    )}s out of ${sessionDuration.toFixed(1)}s). This will remove ${(
                        100 - trimPercentage
                    ).toFixed(1)}% of your data. Please verify your trim range is correct.`,
                );
                setMessage(
                    `Warning: You are trimming to only ${trimPercentage.toFixed(
                        1,
                    )}% of the session. This will remove ${(100 - trimPercentage).toFixed(
                        1,
                    )}% of your data.`,
                );
                // Don't open confirm dialog yet - let user see the warning
                return;
            }
        }

        setTrimWarning(null);
        setTrimConfirmOpen(true);
    }, [editStartTimestamp, editEndTimestamp, sampledDataIndexed]);

    const confirmTrimSave = useCallback(() => {
        if (!editStartTimestamp || !editEndTimestamp || !upload) {
            return;
        }

        // Log the trim operation for debugging
        console.log('Trimming session:', {
            upload_uuid: upload.uuid,
            start_timestamp: editStartTimestamp,
            end_timestamp: editEndTimestamp,
            duration_seconds: (editEndTimestamp - editStartTimestamp) / 1000,
            start_time: new Date(editStartTimestamp).toISOString(),
            end_time: new Date(editEndTimestamp).toISOString(),
        });

        setIsTrimming(true);
        // Keep the dialog open to show loading state

        trimSession(upload.individual.uuid, upload.uuid, {
            start_timestamp: editStartTimestamp,
            end_timestamp: editEndTimestamp,
        })
            .then(() => {
                setMessage('Session trimmed successfully. Recalculating metrics...');
                setTrimConfirmOpen(false);
                handleCloseEditDialog();
                setIsTrimming(false);
                onRecalculating?.();
            })
            .catch((error: unknown) => {
                console.error('Error trimming session:', error);
                setMessage('Error trimming session. Please try again.');
                setIsTrimming(false);
                setTrimConfirmOpen(false);
            });
    }, [editStartTimestamp, editEndTimestamp, upload, handleCloseEditDialog, onRecalculating]);

    return (
        <>
            {!upload.raw_data_url && (
                <Box sx={{ p: 4 }}>
                    <Typography
                        sx={{ color: grey[700], my: 2 }}
                        variant={'h4'}
                        textAlign={'center'}
                    >
                        Complete Session Data
                    </Typography>
                    <Paper sx={{ p: 8 }}>
                        <Typography variant={'h6'} textAlign={'center'} sx={{ color: grey[500] }}>
                            Raw GPS data is not available for this session. The Complete Session
                            Data chart requires raw GPS data to be uploaded.
                        </Typography>
                    </Paper>
                </Box>
            )}
            {sampledData && (
                <Box sx={{ p: 4 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%',
                            my: 2,
                        }}
                    >
                        <Typography sx={{ color: grey[700] }} variant={'h4'}>
                            Complete Session Data
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Can I={'individual:manage-logs'} this={upload.individual}>
                                {/* Only show edit button for mclloyd (Fiyrpod) data type */}
                                {((upload as any).sprint_data_type === 'mclloyd' ||
                                    (upload as any).sprint_data_type === 'mclloyd-ATV1') && (
                                    <Button
                                        onClick={handleOpenEditDialog}
                                        variant="outlined"
                                        color="primary"
                                        startIcon={<ContentCutIcon />}
                                    >
                                        Edit session
                                    </Button>
                                )}
                            </Can>
                            <Can I={'individual:manage-logs'} this={upload.individual}>
                                <Button
                                    variant="outlined"
                                    startIcon={<ContentCutIcon />}
                                    onClick={() => setExclusionDialogOpen(true)}
                                    sx={{ ml: 1 }}
                                >
                                    Exclusion Zones ({exclusionZones.length})
                                </Button>
                            </Can>
                            <Can I={'individual:manage-logs'} this={upload.individual}>
                                <Button
                                    variant="outlined"
                                    startIcon={
                                        isExporting ? (
                                            <CircularProgress size={16} />
                                        ) : (
                                            <DownloadIcon />
                                        )
                                    }
                                    onClick={handleExportFiltered}
                                    disabled={isExporting}
                                    sx={{ ml: 1 }}
                                >
                                    {isExporting ? 'Exporting...' : 'Export Filtered CSV'}
                                </Button>
                            </Can>
                        </Box>
                    </Box>
                    <Paper
                        sx={{
                            p: 8,
                        }}
                    >
                        <Box sx={{ height: 500 }}>
                            {rawDataLoading && (
                                <Typography variant={'h6'} textAlign={'center'}>
                                    Loading Complete Session Data...
                                </Typography>
                            )}
                            {!rawDataLoading && (
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart
                                        key={chartKey}
                                        height={500}
                                        data={sampledDataIndexed}
                                        margin={{
                                            top: 10,
                                            right: 30,
                                            left: 0,
                                            bottom: 0,
                                        }}
                                        onMouseDown={(e) => {
                                            if (e) {
                                                setRefAreaLeft(+(e.activeLabel ?? ''));
                                            }
                                        }}
                                        onMouseMove={(e) => {
                                            if (e && refAreaLeft) {
                                                setRefAreaRight(+(e.activeLabel ?? ''));
                                            }
                                        }}
                                        onMouseUp={(e) => {
                                            if (e) {
                                                zoom();
                                                setChartKey(Math.random().toString());
                                            }
                                        }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis
                                            domain={['dataMin', 'dataMax']}
                                            type="number"
                                            dataKey="Timestamp"
                                            angle={-60}
                                            tickFormatter={formatTimestampSeconds}
                                            textAnchor="end"
                                            height={100}
                                            style={{
                                                fontSize: '0.75rem',
                                            }}
                                            onClick={(e) => {
                                                console.log(e);
                                            }}
                                        />
                                        <YAxis
                                            name={'Velocity (mph)'}
                                            domain={[0, 'dataMax']}
                                            tickFormatter={formatNumber}
                                        />
                                        <Tooltip
                                            content={(props) => (
                                                <CustomTooltipRawGpsData
                                                    {...props}
                                                    hideDurationPrefix={true}
                                                    distanceUnit={distanceUnit}
                                                />
                                            )}
                                        />
                                        <Area
                                            type="monotone"
                                            dataKey="Velocity (mph)"
                                            stroke="#8884d8"
                                            fill="#8884d8"
                                        />
                                        <Brush
                                            tickFormatter={formatTimestampSeconds}
                                            dataKey="Timestamp"
                                            height={60}
                                            stroke="#8884d8"
                                            startIndex={startIndex}
                                            endIndex={endIndex}
                                            onChange={brushChangeHandler}
                                        />
                                        {refAreaLeft && refAreaRight ? (
                                            <ReferenceArea
                                                x1={refAreaLeft}
                                                x2={refAreaRight}
                                                strokeOpacity={0.3}
                                            />
                                        ) : null}
                                        {crops.map((crop) => (
                                            <React.Fragment key={crop.uuid}>
                                                <ReferenceLine
                                                    x={crop.start_timestamp}
                                                    stroke="red"
                                                    strokeDasharray="3 3"
                                                />
                                                <ReferenceLine
                                                    x={crop.end_timestamp}
                                                    stroke="red"
                                                    strokeDasharray="3 3"
                                                />
                                            </React.Fragment>
                                        ))}
                                        {exclusionZones
                                            .filter((ez) => isCropValid(ez))
                                            .map((ez) => (
                                                <ReferenceArea
                                                    key={`exclusion-${ez.uuid}`}
                                                    x1={ez.start_timestamp}
                                                    x2={ez.end_timestamp}
                                                    fill="rgba(128, 128, 128, 0.35)"
                                                    strokeOpacity={0}
                                                    ifOverflow="hidden"
                                                />
                                            ))}
                                    </AreaChart>
                                </ResponsiveContainer>
                            )}
                        </Box>
                        {sampledDataIndexed.length > 0 && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                }}
                            >
                                <Typography
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        textAlign: 'center',
                                    }}
                                >
                                    <IconButton
                                        disabled={startIndex === 0}
                                        onClick={() => {
                                            setStartIndex((startIndex) => startIndex - 1);
                                            setChartKey(Math.random().toString());
                                        }}
                                    >
                                        <ArrowLeft />
                                    </IconButton>
                                    <IconButton
                                        disabled={endIndex === startIndex}
                                        onClick={() => {
                                            setStartIndex((startIndex) => startIndex + 1);
                                            setChartKey(Math.random().toString());
                                        }}
                                    >
                                        <ArrowRight />
                                    </IconButton>
                                    Move Sliders Left And Right Or Use The Arrows To Create
                                    <IconButton
                                        disabled={endIndex === startIndex}
                                        onClick={() => {
                                            setEndIndex((endIndex) => endIndex - 1);
                                            setChartKey(Math.random().toString());
                                        }}
                                    >
                                        <ArrowLeft />
                                    </IconButton>
                                    <IconButton
                                        disabled={endIndex === sampledDataIndexed.length - 1}
                                        onClick={() => {
                                            setEndIndex((endIndex) => endIndex + 1);
                                            setChartKey(Math.random().toString());
                                        }}
                                    >
                                        <ArrowRight />
                                    </IconButton>
                                </Typography>
                                <Box>
                                    <Button
                                        onClick={() => {
                                            setStartIndex(0);
                                            setEndIndex(sampledDataIndexed.length - 1);
                                            setChartKey(Math.random().toString());
                                        }}
                                    >
                                        Reset Zoom
                                    </Button>
                                    <Can I={'individual:manage-logs'} this={upload.individual}>
                                        <Button onClick={() => setSaveCropOpen(true)}>
                                            Save Crop <OpenInNew />
                                        </Button>
                                    </Can>
                                </Box>
                            </Box>
                        )}
                    </Paper>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={12}>
                            <TableContainer sx={{ p: 8, my: 4 }} component={Paper}>
                                <TypographyTitle>Crops</TypographyTitle>
                                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Label</TableCell>
                                            <TableCell align="right">Time</TableCell>
                                            <TableCell align="right">Duration</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {crops
                                            .filter((crop) => isCropValid(crop))
                                            .map((crop) => (
                                                <TableRow
                                                    key={crop.uuid}
                                                    sx={{
                                                        '&:last-child td, &:last-child th': {
                                                            border: 0,
                                                        },
                                                    }}
                                                >
                                                    <TableCell component="th" scope="row">
                                                        <Button
                                                            onClick={() => {
                                                                const cropData =
                                                                    getDataForCrop(crop);
                                                                setSelectedCrops({
                                                                    ...selectedCrops,
                                                                    [crop.uuid]: {
                                                                        crop: crop,
                                                                        data: cropData,
                                                                        startIndex: 0,
                                                                        endIndex:
                                                                            cropData.length - 1,
                                                                        isEditing: false,
                                                                    },
                                                                });
                                                            }}
                                                        >
                                                            {crop.label}
                                                        </Button>
                                                    </TableCell>

                                                    <TableCell align="right">
                                                        {DateTime.fromMillis(
                                                            crop.start_timestamp,
                                                        ).toFormat('h:mm:ss.S a')}{' '}
                                                        to{' '}
                                                        {DateTime.fromMillis(
                                                            crop.end_timestamp,
                                                        ).toFormat('h:mm:ss.S a')}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {(
                                                            (crop.end_timestamp -
                                                                crop.start_timestamp) /
                                                            1000
                                                        ).toFixed(1)}
                                                        s
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Can
                                                            I={'individual:manage-logs'}
                                                            this={upload.individual}
                                                        >
                                                            <Button
                                                                onClick={() => {
                                                                    setCrops((c) =>
                                                                        c.filter(
                                                                            (c) =>
                                                                                c.uuid !==
                                                                                crop.uuid,
                                                                        ),
                                                                    );
                                                                    deleteCrop(
                                                                        upload.individual.uuid,
                                                                        upload.uuid,
                                                                        crop.uuid,
                                                                    )
                                                                        .then(() => {})
                                                                        .catch(() => {});
                                                                }}
                                                            >
                                                                <DeleteIcon />
                                                            </Button>
                                                        </Can>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        {crops.length === 0 && (
                                            <TableRow
                                                sx={{
                                                    '&:last-child td, &:last-child th': {
                                                        border: 0,
                                                    },
                                                }}
                                            >
                                                <TableCell colSpan={3} component="th" scope="row">
                                                    No Crops Have Been Created Yet. Move the slider
                                                    and create some crops.
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <Stack spacing={4}>
                                {Object.keys(selectedCrops).length === 0 && (
                                    <Paper
                                        sx={{
                                            my: 4,
                                            height: 200,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <TypographyTitle>
                                            Choose One (or more) Crops To View And Compare
                                        </TypographyTitle>
                                    </Paper>
                                )}
                                {Object.keys(selectedCrops).map((k: string) => (
                                    <Paper key={k} sx={{ p: 8, my: 4 }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                            }}
                                        >
                                            {!selectedCrops[k].isEditing && (
                                                <Typography variant="h6">
                                                    {selectedCrops[k].crop.label}{' '}
                                                    <Typography variant="caption">
                                                        {DateTime.fromMillis(
                                                            selectedCrops[k].crop.start_timestamp,
                                                        ).toFormat('h:mm:ss.S a')}
                                                    </Typography>
                                                </Typography>
                                            )}
                                            {selectedCrops[k].isEditing && (
                                                <Formik
                                                    initialValues={{
                                                        label: selectedCrops[k].crop.label,
                                                    }}
                                                    onSubmit={(values) => {
                                                        setSelectedCrops((oldCrops) => {
                                                            const newCrops = { ...oldCrops };
                                                            newCrops[k].crop.label = values.label;
                                                            newCrops[k].isEditing = false;
                                                            return newCrops;
                                                        });
                                                        saveCrop(k);
                                                    }}
                                                >
                                                    {({ values, setFieldValue }) => (
                                                        <Form>
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 1,
                                                                }}
                                                            >
                                                                <TextField
                                                                    size={'small'}
                                                                    margin="dense"
                                                                    id="label"
                                                                    type="text"
                                                                    variant="outlined"
                                                                    value={values.label}
                                                                    onChange={(e) => {
                                                                        setFieldValue(
                                                                            'label',
                                                                            e.target.value,
                                                                        );
                                                                    }}
                                                                />
                                                                <Button
                                                                    type={'submit'}
                                                                    variant={'contained'}
                                                                    sx={{ marginLeft: 4 }}
                                                                >
                                                                    Save
                                                                </Button>
                                                            </Box>
                                                        </Form>
                                                    )}
                                                </Formik>
                                            )}

                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                }}
                                            >
                                                {' '}
                                                {/* This Box wraps the two buttons */}
                                                <Button
                                                    onClick={() => {
                                                        setSelectedCrops((crops) => {
                                                            const newCrops = { ...crops };
                                                            delete newCrops[k];
                                                            return newCrops;
                                                        });
                                                    }}
                                                >
                                                    <VisibilityOff />
                                                </Button>
                                                <IconButton
                                                    onClick={() => {
                                                        setSelectedCrops((crops) => {
                                                            const newCrops = { ...crops };
                                                            newCrops[k].isEditing =
                                                                !newCrops[k].isEditing;
                                                            return newCrops;
                                                        });
                                                    }}
                                                >
                                                    {!selectedCrops[k].isEditing ? (
                                                        <>
                                                            <Typography variant={'caption'}>
                                                                Edit
                                                            </Typography>
                                                            <StrikedEditIcon />
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Typography variant={'caption'}>
                                                                Close
                                                            </Typography>
                                                            <EditIcon />
                                                        </>
                                                    )}
                                                </IconButton>
                                            </Box>
                                        </Box>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <CropDataTable data={selectedCrops[k].data} />
                                        </Box>

                                        <Box
                                            style={{
                                                width: '100%',
                                                height: 450,
                                                backgroundColor: '#D9DADF',
                                                borderRadius: 20,
                                                border: '0px solid #A7ABB7',
                                                background:
                                                    'linear-gradient(to bottom, rgba(0,0,0,.011), rgba(217,218,223,.59))',
                                                padding: 20,
                                            }}
                                        >
                                            <ResponsiveContainer width="100%" height="100%">
                                                <ComposedChart
                                                    height={350}
                                                    margin={{
                                                        top: 10,
                                                        right: 30,
                                                        left: 0,
                                                        bottom: 0,
                                                    }}
                                                    data={selectedCrops[k].data}
                                                >
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <YAxis
                                                        yAxisId="left"
                                                        label={{
                                                            value: 'Velocity (mph)',
                                                            angle: -90,
                                                            position: 'insideBottomLeft',
                                                            offset: 20,
                                                        }}
                                                        domain={[0, 'dataMax']}
                                                        tickFormatter={formatNumber}
                                                        style={{
                                                            fontSize: '0.75rem',
                                                        }}
                                                    />
                                                    <YAxis
                                                        yAxisId="right"
                                                        label={{
                                                            value: 'Cumulative Distance (yd)',
                                                            angle: 90,
                                                            position: 'insideBottomRight',
                                                            offset: 20,
                                                            align: 'center',
                                                            style: {
                                                                whiteSpace: 'nowrap', // Prevents text from wrapping
                                                            },
                                                        }}
                                                        orientation="right"
                                                        domain={[0, 'dataMax']}
                                                        tickFormatter={formatNumber}
                                                        style={{
                                                            fontSize: '0.75rem',
                                                        }}
                                                    />
                                                    {hasAtLeastOneHeartRateValue() && (
                                                        <YAxis
                                                            yAxisId="heartRate"
                                                            label={{
                                                                value: 'Heart Rate (bpm)',
                                                                angle: 90,
                                                                position: 'insideBottomRight',
                                                                offset: 20,
                                                                align: 'center',
                                                                style: {
                                                                    whiteSpace: 'nowrap', // Prevents text from wrapping
                                                                },
                                                            }}
                                                            orientation="right"
                                                            domain={['dataMin - 10', 'dataMax']}
                                                            style={{
                                                                fontSize: '0.75rem',
                                                            }}
                                                        />
                                                    )}
                                                    <XAxis
                                                        type={'number'}
                                                        dataKey="Duration (s)"
                                                        scale={'sequential'}
                                                        label={{
                                                            value: 'Duration (s)',
                                                            position: 'insideBottom',
                                                            offset: 20,
                                                        }}
                                                        angle={-90}
                                                        textAnchor="end"
                                                        height={100}
                                                        style={{
                                                            fontSize: '0.75rem',
                                                        }}
                                                    />
                                                    <Tooltip
                                                        content={(props) => (
                                                            <CustomTooltipRawGpsData
                                                                {...props}
                                                                formatTimestampFunction={
                                                                    formatTimestampTenths
                                                                }
                                                            />
                                                        )}
                                                    />
                                                    <Scatter
                                                        yAxisId={'left'}
                                                        dataKey="Velocity (mph)"
                                                        fill="#25C3F1"
                                                    />
                                                    {hasAtLeastOneHeartRateValue() && (
                                                        <Scatter
                                                            yAxisId={'heartRate'}
                                                            dataKey="Heart Rate (bpm)"
                                                            fill={'#FF0000'}
                                                        />
                                                    )}
                                                    <Bar
                                                        yAxisId={'right'}
                                                        dataKey={'Cumulative Distance (yd)'}
                                                        fill="#7794C5"
                                                        barSize={0.2}
                                                        type={'monotone'}
                                                    />
                                                    {selectedCrops[k].isEditing && (
                                                        <Brush
                                                            startIndex={selectedCrops[k].startIndex}
                                                            endIndex={selectedCrops[k].endIndex}
                                                            tickFormatter={formatTimestampTenths}
                                                            dataKey="Timestamp"
                                                            height={40}
                                                            stroke="#8884d8"
                                                            onChange={(newIndex: any): void => {
                                                                setSelectedCrops((crops) => {
                                                                    const newCrops = { ...crops };
                                                                    newCrops[k].startIndex =
                                                                        newIndex.startIndex;
                                                                    newCrops[k].endIndex =
                                                                        newIndex.endIndex;
                                                                    return newCrops;
                                                                });
                                                            }}
                                                        />
                                                    )}
                                                </ComposedChart>
                                            </ResponsiveContainer>
                                        </Box>
                                        {selectedCrops[k].isEditing && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    Move Sliders Left And Right And Press Save Below
                                                </Typography>
                                                <Lottie
                                                    animationData={saveButton} // Correct property name
                                                    loop={true}
                                                    autoplay={true}
                                                    style={{
                                                        width: '200px', // Adjust the width and height as needed
                                                        height: '100px',
                                                    }}
                                                    onClick={() => {
                                                        saveCrop(k);
                                                    }}
                                                ></Lottie>
                                            </Box>
                                        )}
                                    </Paper>
                                ))}
                            </Stack>
                        </Grid>
                    </Grid>
                    <Dialog
                        open={saveCropOpen}
                        onClose={() => setSaveCropOpen(false)}
                        onKeyUp={(e) => {
                            if (e.key === 'Enter') {
                                handleDialogSubmit();
                                setSaveCropOpen(false);
                            }
                        }}
                    >
                        <DialogTitle>Save Crop</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                To save this crop enter a custom label.
                            </DialogContentText>
                            <TextField
                                margin="dense"
                                id="name"
                                label="Custom Label"
                                type="email"
                                fullWidth
                                variant="standard"
                                value={cropLabel}
                                onChange={(e) => {
                                    setCropLabel(e.target.value);
                                }}
                            />
                            {endIndex - startIndex > 500 && (
                                <Typography
                                    sx={{
                                        color: 'red',
                                    }}
                                >
                                    ** Crop must be smaller
                                </Typography>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button type={'button'} onClick={() => setSaveCropOpen(false)}>
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                onClick={() => handleDialogSubmit()}
                                disabled={endIndex - startIndex > 500 || !cropLabel}
                            >
                                Save Crop
                            </Button>
                        </DialogActions>
                    </Dialog>
                    <Dialog
                        open={editDialogOpen}
                        onClose={handleCloseEditDialog}
                        maxWidth="lg"
                        fullWidth
                    >
                        <DialogTitle>Edit Session Data</DialogTitle>
                        <DialogContent>
                            <Box sx={{ px: 3, py: 3 }}>
                                {trimWarning && (
                                    <Box
                                        sx={{
                                            mb: 3,
                                            p: 2,
                                            bgcolor: 'warning.light',
                                            borderRadius: 1,
                                            border: '1px solid',
                                            borderColor: 'warning.main',
                                        }}
                                    >
                                        <Typography variant="body2" color="warning.dark">
                                            {trimWarning}
                                        </Typography>
                                    </Box>
                                )}
                                <Box
                                    sx={{ mb: 6, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}
                                >
                                    <Typography variant="body2" color="primary.contrastText">
                                        Drag the green (start) and red (end) lines to trim the
                                        session. Data outside the selected range will be removed.
                                    </Typography>
                                </Box>
                                <Box sx={{ height: 500, mt: 6 }}>
                                    {rawDataLoading && (
                                        <Typography variant={'h6'} textAlign={'center'}>
                                            Loading Complete Session Data...
                                        </Typography>
                                    )}
                                    {!rawDataLoading && (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <AreaChart
                                                key={chartKey}
                                                height={500}
                                                data={sampledDataIndexed}
                                                margin={{
                                                    top: 40,
                                                    right: 30,
                                                    left: 0,
                                                    bottom: 0,
                                                }}
                                                onMouseDown={(e) => {
                                                    if (
                                                        e &&
                                                        isEditMode &&
                                                        editStartTimestamp &&
                                                        editEndTimestamp
                                                    ) {
                                                        // Handle dragging trim handles in edit mode
                                                        const clickedTimestamp = +(
                                                            e.activeLabel ?? ''
                                                        );
                                                        const startDistance = Math.abs(
                                                            clickedTimestamp - editStartTimestamp,
                                                        );
                                                        const endDistance = Math.abs(
                                                            clickedTimestamp - editEndTimestamp,
                                                        );

                                                        // If click is near a handle, we'll track it for dragging
                                                        // Use a larger threshold to make handles easier to grab
                                                        const timeRange =
                                                            editEndTimestamp - editStartTimestamp;
                                                        const threshold = Math.max(
                                                            timeRange * 0.1, // 10% of range (increased from 5%)
                                                            sampledDataIndexed &&
                                                                sampledDataIndexed.length > 0
                                                                ? (timeRange /
                                                                      sampledDataIndexed.length) *
                                                                      10
                                                                : timeRange * 0.1, // Or 10 data points worth, fallback to 10%
                                                        );
                                                        if (startDistance < threshold) {
                                                            // Dragging start handle
                                                            setRefAreaLeft(editStartTimestamp);
                                                        } else if (endDistance < threshold) {
                                                            // Dragging end handle
                                                            setRefAreaRight(editEndTimestamp);
                                                        }
                                                    }
                                                }}
                                                onMouseMove={(e) => {
                                                    // Only process mouse move when actually dragging (refAreaLeft or refAreaRight is set)
                                                    if (
                                                        e &&
                                                        isEditMode &&
                                                        sampledDataIndexed &&
                                                        sampledDataIndexed.length > 0 &&
                                                        (refAreaLeft !== undefined ||
                                                            refAreaRight !== undefined)
                                                    ) {
                                                        // Get timestamp from activePayload (actual data point) or activeLabel
                                                        let newTimestamp: number | null = null;

                                                        // Try activePayload first - this is the actual data point under the cursor
                                                        if (
                                                            e.activePayload &&
                                                            e.activePayload.length > 0
                                                        ) {
                                                            const payload = e.activePayload[0];
                                                            if (
                                                                payload.payload &&
                                                                payload.payload['Timestamp']
                                                            ) {
                                                                newTimestamp =
                                                                    payload.payload['Timestamp'];
                                                            }
                                                        }

                                                        // Fallback to activeLabel if activePayload doesn't have timestamp
                                                        if (
                                                            (newTimestamp === null ||
                                                                isNaN(newTimestamp)) &&
                                                            e.activeLabel
                                                        ) {
                                                            newTimestamp = +(e.activeLabel ?? '');
                                                        }

                                                        // Final fallback: use the closest data point to mouse position
                                                        if (
                                                            (newTimestamp === null ||
                                                                isNaN(newTimestamp)) &&
                                                            e.activeCoordinate
                                                        ) {
                                                            // Find closest data point by comparing X coordinates
                                                            const mouseX = e.activeCoordinate.x;
                                                            let closestPoint =
                                                                sampledDataIndexed[0];
                                                            let minDistance = Infinity;

                                                            // This is approximate - we'd need the actual chart width
                                                            // For now, use the data point index based on mouse position
                                                            if (sampledDataIndexed.length > 1) {
                                                                const dataMin =
                                                                    sampledDataIndexed[0][
                                                                        'Timestamp'
                                                                    ];
                                                                const dataMax =
                                                                    sampledDataIndexed[
                                                                        sampledDataIndexed.length -
                                                                            1
                                                                    ]['Timestamp'];
                                                                const dataRange = dataMax - dataMin;

                                                                // Estimate which data point we're closest to
                                                                // This is a rough approximation
                                                                for (const point of sampledDataIndexed) {
                                                                    const pointX =
                                                                        ((point['Timestamp'] -
                                                                            dataMin) /
                                                                            dataRange) *
                                                                        1000; // Normalize to 0-1000
                                                                    const distance = Math.abs(
                                                                        mouseX - pointX,
                                                                    );
                                                                    if (distance < minDistance) {
                                                                        minDistance = distance;
                                                                        closestPoint = point;
                                                                    }
                                                                }
                                                                newTimestamp =
                                                                    closestPoint['Timestamp'];
                                                            }
                                                        }

                                                        if (
                                                            newTimestamp !== null &&
                                                            !isNaN(newTimestamp)
                                                        ) {
                                                            if (
                                                                refAreaLeft !== undefined &&
                                                                newTimestamp <
                                                                    (editEndTimestamp ?? Infinity)
                                                            ) {
                                                                // Dragging start handle
                                                                setEditStartTimestamp(newTimestamp);
                                                            } else if (
                                                                refAreaRight !== undefined &&
                                                                newTimestamp >
                                                                    (editStartTimestamp ?? 0)
                                                            ) {
                                                                // Dragging end handle
                                                                setEditEndTimestamp(newTimestamp);
                                                            }
                                                        }
                                                    }
                                                }}
                                                onMouseUp={(e) => {
                                                    if (e && isEditMode) {
                                                        // Clear drag state
                                                        setRefAreaLeft(undefined);
                                                        setRefAreaRight(undefined);
                                                        setChartKey(Math.random().toString());
                                                    }
                                                }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis
                                                    domain={['dataMin', 'dataMax']}
                                                    type="number"
                                                    dataKey="Timestamp"
                                                    angle={-60}
                                                    tickFormatter={formatTimestampSeconds}
                                                    textAnchor="end"
                                                    height={100}
                                                    style={{
                                                        fontSize: '0.75rem',
                                                    }}
                                                />
                                                <YAxis
                                                    name={'Velocity (mph)'}
                                                    domain={[0, 'dataMax']}
                                                    tickFormatter={formatNumber}
                                                />
                                                <Tooltip
                                                    content={(props) => (
                                                        <CustomTooltipRawGpsData
                                                            {...props}
                                                            hideDurationPrefix={true}
                                                            distanceUnit={distanceUnit}
                                                        />
                                                    )}
                                                />
                                                <Area
                                                    type="monotone"
                                                    dataKey="Velocity (mph)"
                                                    stroke="#8884d8"
                                                    fill="#8884d8"
                                                />
                                                {/* Shaded areas for data outside trim range in edit mode */}
                                                {isEditMode &&
                                                    editStartTimestamp &&
                                                    editEndTimestamp &&
                                                    (() => {
                                                        const firstTimestamp =
                                                            sampledDataIndexed[0]?.['Timestamp'] ??
                                                            0;
                                                        const lastTimestamp =
                                                            sampledDataIndexed[
                                                                sampledDataIndexed.length - 1
                                                            ]?.['Timestamp'] ?? 0;
                                                        return (
                                                            <>
                                                                {firstTimestamp <
                                                                    editStartTimestamp && (
                                                                    <ReferenceArea
                                                                        x1={firstTimestamp}
                                                                        x2={editStartTimestamp}
                                                                        strokeOpacity={0.2}
                                                                        fill="rgba(255, 0, 0, 0.1)"
                                                                    />
                                                                )}
                                                                {lastTimestamp >
                                                                    editEndTimestamp && (
                                                                    <ReferenceArea
                                                                        x1={editEndTimestamp}
                                                                        x2={lastTimestamp}
                                                                        strokeOpacity={0.2}
                                                                        fill="rgba(255, 0, 0, 0.1)"
                                                                    />
                                                                )}
                                                            </>
                                                        );
                                                    })()}
                                                {/* Trim handles in edit mode */}
                                                {isEditMode && editStartTimestamp && (
                                                    <>
                                                        <ReferenceLine
                                                            x={editStartTimestamp}
                                                            stroke="green"
                                                            strokeWidth={3}
                                                            label={{
                                                                value: 'Start',
                                                                position: 'top',
                                                                fill: 'green',
                                                                fontSize: 12,
                                                                offset: 15,
                                                            }}
                                                        />
                                                        <Scatter
                                                            yAxisId={0}
                                                            data={[
                                                                {
                                                                    Timestamp: editStartTimestamp,
                                                                    'Velocity (mph)':
                                                                        sampledDataIndexed &&
                                                                        sampledDataIndexed.length >
                                                                            0
                                                                            ? Math.max(
                                                                                  ...sampledDataIndexed.map(
                                                                                      (d) =>
                                                                                          d[
                                                                                              'Velocity (mph)'
                                                                                          ] || 0,
                                                                                  ),
                                                                              ) / 2
                                                                            : 0,
                                                                },
                                                            ]}
                                                            shape={RoundedRectHandle as any}
                                                            fill="green"
                                                            stroke="green"
                                                            strokeWidth={2}
                                                        />
                                                    </>
                                                )}
                                                {isEditMode && editEndTimestamp && (
                                                    <>
                                                        <ReferenceLine
                                                            x={editEndTimestamp}
                                                            stroke="red"
                                                            strokeWidth={3}
                                                            label={{
                                                                value: 'End',
                                                                position: 'top',
                                                                fill: 'red',
                                                                fontSize: 12,
                                                                offset: 15,
                                                            }}
                                                        />
                                                        <Scatter
                                                            yAxisId={0}
                                                            data={[
                                                                {
                                                                    Timestamp: editEndTimestamp,
                                                                    'Velocity (mph)':
                                                                        sampledDataIndexed &&
                                                                        sampledDataIndexed.length >
                                                                            0
                                                                            ? Math.max(
                                                                                  ...sampledDataIndexed.map(
                                                                                      (d) =>
                                                                                          d[
                                                                                              'Velocity (mph)'
                                                                                          ] || 0,
                                                                                  ),
                                                                              ) / 2
                                                                            : 0,
                                                                },
                                                            ]}
                                                            shape={RoundedRectHandle as any}
                                                            fill="red"
                                                            stroke="red"
                                                            strokeWidth={2}
                                                        />
                                                    </>
                                                )}
                                                {crops
                                                    .filter((crop) => isCropValid(crop))
                                                    .map((crop) => (
                                                        <React.Fragment key={crop.uuid}>
                                                            <ReferenceLine
                                                                x={crop.start_timestamp}
                                                                stroke="red"
                                                                strokeDasharray="3 3"
                                                            />
                                                            <ReferenceLine
                                                                x={crop.end_timestamp}
                                                                stroke="red"
                                                                strokeDasharray="3 3"
                                                            />
                                                        </React.Fragment>
                                                    ))}
                                                {exclusionZones
                                                    .filter((ez) => isCropValid(ez))
                                                    .map((ez) => (
                                                        <ReferenceArea
                                                            key={`exclusion-${ez.uuid}`}
                                                            x1={ez.start_timestamp}
                                                            x2={ez.end_timestamp}
                                                            fill="rgba(128, 128, 128, 0.35)"
                                                            strokeOpacity={0}
                                                            ifOverflow="hidden"
                                                        />
                                                    ))}
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    )}
                                </Box>
                                {sampledDataIndexed.length > 0 && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            gap: 2,
                                            alignItems: 'center',
                                            mt: 2,
                                        }}
                                    >
                                        <Typography variant="body2" sx={{ color: grey[600] }}>
                                            {editStartTimestamp &&
                                                editEndTimestamp &&
                                                `Trim Range: ${DateTime.fromMillis(
                                                    editStartTimestamp,
                                                ).toFormat('h:mm:ss.S a')} - ${DateTime.fromMillis(
                                                    editEndTimestamp,
                                                ).toFormat('h:mm:ss.S a')}`}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </DialogContent>
                        <DialogActions
                            sx={{
                                padding: '32px !important',
                                display: 'flex',
                                gap: 1.5,
                                justifyContent: 'flex-end',
                            }}
                        >
                            <Button
                                onClick={handleCloseEditDialog}
                                variant="outlined"
                                startIcon={<CancelIcon />}
                                disabled={isTrimming}
                            >
                                Cancel
                            </Button>
                            <Can I={'individual:manage-logs'} this={upload.individual}>
                                <Button
                                    onClick={handleTrimSave}
                                    variant="contained"
                                    color="primary"
                                    startIcon={<SaveIcon />}
                                    disabled={
                                        isTrimming ||
                                        !editStartTimestamp ||
                                        !editEndTimestamp ||
                                        editStartTimestamp >= editEndTimestamp ||
                                        (originalStartTimestamp === editStartTimestamp &&
                                            originalEndTimestamp === editEndTimestamp)
                                    }
                                >
                                    {isTrimming ? 'Trimming...' : 'Save Trim'}
                                </Button>
                            </Can>
                        </DialogActions>
                    </Dialog>
                    <Dialog
                        open={trimConfirmOpen}
                        onClose={() => !isTrimming && setTrimConfirmOpen(false)}
                    >
                        <DialogTitle>
                            {isTrimming ? 'Trimming Session...' : 'Confirm Session Trim'}
                        </DialogTitle>
                        <DialogContent sx={{ pt: 1 }}>
                            {isTrimming ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: 3,
                                    }}
                                >
                                    <DialogContentText sx={{ textAlign: 'center', mb: 6 }}>
                                        Processing your session trim. This may take 40-60 seconds to
                                        complete.
                                    </DialogContentText>
                                    <CircularProgress
                                        size={48}
                                        thickness={4}
                                        sx={{
                                            color: 'primary.main',
                                        }}
                                    />
                                </Box>
                            ) : (
                                <>
                                    <DialogContentText>
                                        This will permanently trim the session data and remove all
                                        data outside the selected range. This action will:
                                        <ul>
                                            <li>Reprocess the session with the trimmed data</li>
                                            <li>Recalculate all sprints, motions, and summaries</li>
                                            <li>
                                                Delete or adjust any crops outside the new range
                                            </li>
                                        </ul>
                                        This action cannot be undone. Processing may take 40-60
                                        seconds. Do you want to continue?
                                    </DialogContentText>
                                    {editStartTimestamp && editEndTimestamp && (
                                        <Typography
                                            variant="body2"
                                            sx={{ mt: 2, color: grey[600] }}
                                        >
                                            <strong>Trim Range:</strong>{' '}
                                            {DateTime.fromMillis(editStartTimestamp).toFormat(
                                                'h:mm:ss.S a',
                                            )}{' '}
                                            to{' '}
                                            {DateTime.fromMillis(editEndTimestamp).toFormat(
                                                'h:mm:ss.S a',
                                            )}
                                        </Typography>
                                    )}
                                </>
                            )}
                        </DialogContent>
                        {!isTrimming && (
                            <DialogActions
                                sx={{
                                    padding: '24px !important',
                                    display: 'flex',
                                    gap: 1.5,
                                    justifyContent: 'flex-end',
                                }}
                            >
                                <Button onClick={() => setTrimConfirmOpen(false)}>Cancel</Button>
                                <Button
                                    onClick={confirmTrimSave}
                                    color="primary"
                                    variant="contained"
                                >
                                    Confirm Trim
                                </Button>
                            </DialogActions>
                        )}
                    </Dialog>
                </Box>
            )}
            <ExclusionZoneDialog
                open={exclusionDialogOpen}
                onClose={(changed: boolean) => {
                    setExclusionDialogOpen(false);
                    if (changed) {
                        getAllCrops(upload.individual.uuid, upload.uuid).then((cropsResponse) => {
                            const allCrops = cropsResponse.data;
                            const isExcl = (label: string) =>
                                label.startsWith('__exclusion__') || label.startsWith('__trim__');
                            setExclusionZones(allCrops.filter((c) => isExcl(c.label)));
                            setCrops(sortCrops(allCrops.filter((c) => !isExcl(c.label))));
                        });
                        onRecalculating?.();
                    }
                }}
                upload={upload}
                sampledData={sampledDataIndexed}
                rawData={rawData}
                distanceUnit={distanceUnit}
            />
            <Snackbar
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                open={!!message}
                autoHideDuration={1500}
                onClose={() => setMessage('')}
                message={message}
                action={
                    <React.Fragment>
                        <IconButton
                            size="small"
                            aria-label="close"
                            color="inherit"
                            onClick={() => setMessage('')}
                        >
                            <CloseIcon />
                        </IconButton>
                    </React.Fragment>
                }
            />
        </>
    );
};
export default RawDataViewer;
